import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatNumber, formatDate } from '../utils/formatters';
import { Search, Package, Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function StockCardPage() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockCard, setStockCard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setProductsLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

  useEffect(() => {
    const productId = searchParams.get('product_id');
    if (productId) {
      fetchProductById(productId);
    }
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      fetchStockCard();
    }
  }, [selectedProduct, dateFrom, dateTo, pagination.page]);

  const fetchProducts = async (search = '') => {
    setProductsLoading(true);
    try {
      const params = queryParams({ limit: 50, search });
      const res = await api.get('/products/list', { params });
      setProducts(res.data.data?.products || res.data.data || []);
    } catch (error) {
      // Fallback to /products
      try {
        const params = queryParams({ limit: 50, search });
        const res = await api.get('/products', { params });
        setProducts(res.data.data?.products || res.data.data || []);
      } catch (err) {
        handleApiError(err);
      }
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchProductById = async (id) => {
    try {
      const res = await api.get(`/products/${id}`);
      if (res.data.success) {
        setSelectedProduct(res.data.data);
        setProductSearch(res.data.data.name || '');
      }
    } catch (error) {
      // Silently fail
    }
  };

  const fetchStockCard = async () => {
    if (!selectedProduct) return;
    setLoading(true);
    try {
      const params = queryParams({
        page: pagination.page,
        limit: pagination.limit,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      const res = await api.get(`/inventory/stock-card/${selectedProduct.id}`, { params });
      if (res.data.success) {
        setStockCard(res.data.data?.movements || res.data.data?.items || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    if (!productSearch) return products.slice(0, 20);
    const search = productSearch.toLowerCase();
    return products
      .filter((p) => p.name?.toLowerCase().includes(search) || p.sku?.toLowerCase().includes(search))
      .slice(0, 20);
  }, [products, productSearch]);

  const handleProductSearch = (value) => {
    setProductSearch(value);
    setShowProductDropdown(true);
    if (value.length >= 2) {
      fetchProducts(value);
    }
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.name || '');
    setShowProductDropdown(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearProduct = () => {
    setSelectedProduct(null);
    setProductSearch('');
    setStockCard([]);
    setPagination({ page: 1, limit: 50, total: 0, totalPages: 0 });
  };

  const typeBadge = (type) => {
    const map = {
      in: 'badge-success',
      masuk: 'badge-success',
      out: 'badge-danger',
      keluar: 'badge-danger',
      adjustment: 'badge-info',
      penyesuaian: 'badge-info',
      transfer_in: 'badge-info',
      transfer_out: 'badge-warning',
    };
    return map[type?.toLowerCase()] || 'badge-gray';
  };

  const typeLabel = (type) => {
    const map = {
      in: 'Masuk',
      masuk: 'Masuk',
      out: 'Keluar',
      keluar: 'Keluar',
      adjustment: 'Penyesuaian',
      penyesuaian: 'Penyesuaian',
      transfer_in: 'Transfer Masuk',
      transfer_out: 'Transfer Keluar',
    };
    return map[type?.toLowerCase()] || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Kartu Stok</h1>
          <p className="page-subtitle">Riwayat pergerakan stok untuk setiap produk</p>
        </div>
      </div>

      {/* Product Selector & Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Searchable Product Dropdown */}
            <div className="relative">
              <label className="label">Pilih Produk</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <input
                  type="text"
                  className="input pl-9 pr-8"
                  placeholder="Cari nama produk atau SKU..."
                  value={productSearch}
                  onChange={(e) => handleProductSearch(e.target.value)}
                  onFocus={() => setShowProductDropdown(true)}
                  onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                />
                {productSearch && (
                  <button onClick={clearProduct} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </button>
                )}
                {showProductDropdown && filteredProducts.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredProducts.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                        onMouseDown={() => selectProduct(p)}
                      >
                        <span className="font-medium text-gray-900 dark:text-white">{p.name}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">({p.sku})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Date From */}
            <div>
              <label className="label">Dari Tanggal</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  className="input pl-9"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPagination((prev) => ({ ...prev, page: 1 })); }}
                />
              </div>
            </div>

            {/* Date To */}
            <div>
              <label className="label">Sampai Tanggal</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  className="input pl-9"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPagination((prev) => ({ ...prev, page: 1 })); }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Card Table */}
      <div className="card">
        {!selectedProduct ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Pilih Produk</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Pilih produk untuk melihat riwayat kartu stok.</p>
          </div>
        ) : loading ? (
          <div className="card-body">
            <div className="flex items-center gap-3 mb-4">
              <div className="skeleton h-5 w-48" />
              <div className="skeleton h-5 w-32" />
            </div>
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-36" />
                </div>
              ))}
            </div>
          </div>
        ) : stockCard.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Tidak ada riwayat pergerakan stok untuk produk ini.</p>
          </div>
        ) : (
          <>
            {/* Product Info Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center text-primary-900 font-semibold">
                  {selectedProduct.sku?.charAt(0) || 'P'}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedProduct.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">SKU: {selectedProduct.sku} | Stok Saat Ini: {formatNumber(selectedProduct.stock)}</p>
                </div>
              </div>
            </div>
            <div className="table-container rounded-none border-0 border-t-0">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Referensi</th>
                    <th>Tipe</th>
                    <th className="text-right">Sebelum</th>
                    <th className="text-right">Qty Perubahan</th>
                    <th className="text-right">Sesudah</th>
                    <th className="text-right">Harga</th>
                    <th>Catatan</th>
                  </tr>
                </thead>
                <tbody>
                  {stockCard.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(item.date || item.created_at)}</span>
                      </td>
                      <td>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.reference || item.doc_number || '-'}</span>
                      </td>
                      <td>
                        <span className={`badge ${typeBadge(item.type || item.movement_type)}`}>
                          {typeLabel(item.type || item.movement_type)}
                        </span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatNumber(item.quantity_before || item.before)}</span>
                      </td>
                      <td className="text-right">
                        <span className={`text-sm font-medium ${(item.quantity_change || item.change) > 0 ? 'text-success-600' : (item.quantity_change || item.change) < 0 ? 'text-danger-600' : 'text-gray-500'}`}>
                          {(item.quantity_change || item.change) > 0 ? '+' : ''}{formatNumber(item.quantity_change || item.change)}
                        </span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatNumber(item.quantity_after || item.after)}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(item.price || item.unit_price)}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-48 block" title={item.notes}>
                          {item.notes || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
